import { useEffect, useState } from "react";
import { CalendarPlus, Loader2, PencilLine, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { DayField } from "@/components/DayField";
import { api, apiError, fmtDay } from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const asDate = (value) => {
  const text = String(value || "");
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const parts = text.split(".");
  return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : today();
};

const BLANK = { date: today(), pct: "", actual_value: "", impact_lakh: "", remark: "" };

/**
 * One day of one usecase — the per cent, the KPI as it read and the rupee impact.
 *
 * It posts to `/projects/{id}/compliance`, the same endpoint the Project Update popup uses, so a
 * day filled here and a day filled there are the same record. `preset` loads a day already on
 * record back into the form, which is how an old reading is corrected.
 */
export const DailyEntryForm = ({ card, onSaved, testId, preset, onCancel }) => {
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!preset) return;
    setForm({
      date: asDate(preset.date),
      pct: preset.pct ?? "",
      actual_value: preset.actual_value ?? "",
      impact_lakh: preset.impact_lakh ?? "",
      remark: preset.remark ?? "",
    });
  }, [preset]);

  const save = async () => {
    if (!form.date) {
      toast.error("Which day is this reading for?");
      return;
    }
    if ([form.pct, form.actual_value, form.impact_lakh].every((v) => v === "")) {
      toast.error("Fill at least one of the compliance per cent, the KPI or the impact.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${card.project_id}/compliance`, {
        date: form.date,
        phase: card.phase || "Go-Live",
        pct: num(form.pct),
        actual_value: num(form.actual_value),
        impact_lakh: num(form.impact_lakh),
        remark: form.remark || null,
      });
      toast.success(
        data.awaiting_verification
          ? "Sent for the unit SPOC's approval — nothing has changed on the odometer yet."
          : `${form.date} saved. The odometer is already reading it.`,
      );
      setForm({ ...BLANK, date: form.date });
      onSaved?.();    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const fields = [
    ["pct", "Compliance %", "number", "92"],
    ["actual_value", `KPI actual${card.kpi.unit ? ` (${card.kpi.unit})` : ""}`, "number", "556.2"],
    ["impact_lakh", "Impact (₹ Lakh)", "number", "19.0"],
  ];

  return (
    <div className="border border-border bg-muted/40 p-4" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="label-caps">
          {preset ? `Correcting ${fmtDay(preset.date)}` : "Fill a day"}
        </div>
        {preset && (
          <button
            type="button"
            data-testid={`${testId}-cancel`}
            onClick={() => {
              setForm(BLANK);
              onCancel?.();
            }}
            className="flex items-center gap-1 font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground transition-colors duration-200 hover:text-foreground"
          >
            <X className="h-3 w-3" /> Leave it as it is
          </button>
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-end gap-3">
        <label className="block w-[160px]">
          <span className="font-data block text-[10px] text-muted-foreground">
            Date this reading is for
          </span>
          <DayField
            className="mt-1"
            testId={`${testId}-date`}
            format="iso"
            max={today()}
            value={form.date}
            onChange={(v) => setForm((f) => ({ ...f, date: v }))}
          />
        </label>
        {fields.map(([key, label, type, hint]) => (
          <label key={key} className="block w-[150px]">
            <span className="font-data block text-[10px] text-muted-foreground">{label}</span>
            <Input
              type={type}
              step="any"
              data-testid={`${testId}-${key}`}
              value={form[key]}
              placeholder={hint}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
              className="mt-1 h-10 rounded-none bg-card"
            />
          </label>
        ))}
        <label className="block min-w-[200px] flex-1">
          <span className="font-data block text-[10px] text-muted-foreground">Remark</span>
          <Input
            data-testid={`${testId}-remark`}
            value={form.remark}
            placeholder="shift average, stoppage, anything worth recording"
            onChange={(e) => setForm((f) => ({ ...f, remark: e.target.value }))}
            className="mt-1 h-10 rounded-none bg-card"
          />
        </label>
        <button
          type="button"
          data-testid={`${testId}-save`}
          disabled={busy}
          onClick={save}
          className="flex h-10 items-center gap-2 bg-foreground px-4 font-display text-[10px] font-bold uppercase tracking-widest text-background transition-opacity duration-200 hover:opacity-90 disabled:opacity-50"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : preset ? (
            <PencilLine className="h-3.5 w-3.5" />
          ) : (
            <CalendarPlus className="h-3.5 w-3.5" />
          )}
          {preset ? "Correct it" : "Record"}
        </button>
      </div>
      <p className="mt-2 text-[10.5px] text-muted-foreground">
        A day filled again corrects the day that is already there — one usecase, one day, one
        record. A figure left blank is read as not measured, never as nought, so clearing a cell
        here takes that figure off the day.
      </p>
    </div>
  );
};
